import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { BusinessActivity } from '@/types/businessAppTypes';
import { ImagePicker } from '../../../shared/ImagePicker';

interface EditActivityModalProps {
  open: boolean;
  onClose: () => void;
  activity: BusinessActivity | null;
  onSave: (data: Partial<BusinessActivity>) => void;
  businessImages?: string[];
}

export function EditActivityModal({
  open,
  onClose,
  activity,
  onSave,
  businessImages = []
}: EditActivityModalProps) {
  const [formData, setFormData] = useState<Partial<BusinessActivity>>({});

  useEffect(() => {
    if (activity) {
      setFormData(activity);
    }
  }, [activity]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  if (!activity) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Edit Activity</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Activity Name</Label>
              <Input
                id="name"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter activity name"
                required
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe your activity"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="activityType">Activity Type</Label>
                <Select
                  value={formData.activityType}
                  onValueChange={(value) => setFormData({ ...formData, activityType: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tour">Tour</SelectItem>
                    <SelectItem value="workshop">Workshop</SelectItem>
                    <SelectItem value="experience">Experience</SelectItem>
                    <SelectItem value="adventure">Adventure</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="maxParticipants">Max Participants</Label>
                <Input
                  id="maxParticipants"
                  type="number"
                  min="1"
                  value={formData.maxParticipants || 0}
                  onChange={(e) => setFormData({ ...formData, maxParticipants: parseInt(e.target.value) })}
                  required
                />
              </div>
            </div>

            <div className="space-y-4">
              <Label>Pricing</Label>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="adultPrice">Adult Price</Label>
                  <Input
                    id="adultPrice"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.pricing?.defaultPrices?.adult?.amount || 0}
                    onChange={(e) => setFormData(prev => {
                      const newAmount = parseFloat(e.target.value);
                      const resolvedCurrency = 
                        prev.pricing?.defaultPrices?.adult?.currency || 
                        prev.pricing?.defaultPrices?.child?.currency || 
                        prev.pricing?.defaultPrices?.student?.currency || 
                        'USD';

                      return {
                        ...prev,
                        pricing: {
                          ...(prev.pricing || {}),
                          defaultPrices: {
                            adult: { amount: newAmount, currency: resolvedCurrency },
                            child: { 
                              amount: prev.pricing?.defaultPrices?.child?.amount || 0, 
                              currency: prev.pricing?.defaultPrices?.child?.currency || resolvedCurrency 
                            },
                            student: { 
                              amount: prev.pricing?.defaultPrices?.student?.amount || 0, 
                              currency: prev.pricing?.defaultPrices?.student?.currency || resolvedCurrency 
                            }
                          }
                        }
                      };
                    })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="childPrice">Child Price</Label>
                  <Input
                    id="childPrice"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.pricing?.defaultPrices?.child?.amount || 0}
                    onChange={(e) => setFormData(prev => {
                      const newAmount = parseFloat(e.target.value);
                      const resolvedCurrency = 
                        prev.pricing?.defaultPrices?.adult?.currency || 
                        prev.pricing?.defaultPrices?.child?.currency || 
                        prev.pricing?.defaultPrices?.student?.currency || 
                        'USD';

                      return {
                        ...prev,
                        pricing: {
                          ...(prev.pricing || {}),
                          defaultPrices: {
                            adult: { 
                              amount: prev.pricing?.defaultPrices?.adult?.amount || 0, 
                              currency: prev.pricing?.defaultPrices?.adult?.currency || resolvedCurrency 
                            },
                            child: { amount: newAmount, currency: resolvedCurrency },
                            student: { 
                              amount: prev.pricing?.defaultPrices?.student?.amount || 0, 
                              currency: prev.pricing?.defaultPrices?.student?.currency || resolvedCurrency 
                            }
                          }
                        }
                      };
                    })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="studentPrice">Student Price</Label>
                  <Input
                    id="studentPrice"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.pricing?.defaultPrices?.student?.amount || 0}
                    onChange={(e) => setFormData(prev => {
                      const newAmount = parseFloat(e.target.value);
                      const resolvedCurrency = 
                        prev.pricing?.defaultPrices?.adult?.currency || 
                        prev.pricing?.defaultPrices?.child?.currency || 
                        prev.pricing?.defaultPrices?.student?.currency || 
                        'USD';

                      return {
                        ...prev,
                        pricing: {
                          ...(prev.pricing || {}),
                          defaultPrices: {
                            adult: { 
                              amount: prev.pricing?.defaultPrices?.adult?.amount || 0, 
                              currency: prev.pricing?.defaultPrices?.adult?.currency || resolvedCurrency 
                            },
                            child: { 
                              amount: prev.pricing?.defaultPrices?.child?.amount || 0, 
                              currency: prev.pricing?.defaultPrices?.child?.currency || resolvedCurrency 
                            },
                            student: { amount: newAmount, currency: resolvedCurrency }
                          }
                        }
                      };
                    })}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Activity Image Section */}
            <ImagePicker
              label="Activity Image"
              description="Choose an image from your library to represent this activity"
              selectedImage={formData.images?.[0]}
              onImageChange={(imageUrl) => {
                setFormData({
                  ...formData,
                  images: imageUrl ? [imageUrl] : []
                });
              }}
              imageLibrary={businessImages}
              placeholder="Select from your image library"
              compact={true}
            />

            <div className="flex items-center space-x-2">
              <Switch
                id="isActive"
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
              />
              <Label htmlFor="isActive">Activity is active and ready for booking</Label>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Save Changes</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
} 